import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mail, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { resendVerificationEmail } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import { Link } from "wouter";

export default function VerifyEmailPending() {
  const [email, setEmail] = useState<string>("");
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(60);

  useEffect(() => {
    // Recuperar email del localStorage
    const pendingEmail = localStorage.getItem("pendingVerificationEmail");
    if (pendingEmail) {
      setEmail(pendingEmail);
    }

    // Limpiar cuando se desmonte
    return () => {
      localStorage.removeItem("pendingVerificationEmail");
    };
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleResend = async () => {
    if (!email) return;

    setIsResending(true);
    try {
      await resendVerificationEmail(email);
      toast({
        title: "✅ Email reenviado",
        description:
          "Hemos enviado un nuevo enlace de verificación a tu email.",
      });
      setCountdown(60);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center px-4">
      <Card className="w-full max-w-md shadow-xl border-primary/10">
        <CardHeader className="text-center border-b bg-muted/10 pb-8">
          <div className="flex justify-center mb-4">
            <Mail className="h-12 w-12 text-primary" />
          </div>
          <CardTitle className="text-2xl font-heading font-bold uppercase tracking-wider">
            Verifica tu email
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 text-center">
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-800 mb-2">
                Hemos enviado un enlace de verificación a:
              </p>
              <p className="font-bold text-lg text-blue-900 break-all">
                {email || "tu correo electrónico"}
              </p>
            </div>

            <div className="space-y-4">
              <p className="text-muted-foreground">
                Por favor, revisa tu bandeja de entrada y haz clic en el enlace
                para activar tu cuenta.
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <p className="font-medium mb-1">📌 ¿No encuentras el email?</p>
                <ul className="list-disc list-inside space-y-1 text-left">
                  <li>Revisa la carpeta de spam o correo no deseado</li>
                  <li>Espera unos minutos, puede tardar en llegar</li>
                  <li>Verifica que hayas escrito el email correctamente</li>
                </ul>
              </div>

              <Button
                onClick={handleResend}
                disabled={isResending || countdown > 0}
                className="w-full"
                variant="outline"
              >
                {isResending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enviando...
                  </>
                ) : countdown > 0 ? (
                  `Reenviar email (${countdown}s)`
                ) : (
                  "Reenviar email de verificación"
                )}
              </Button>

              <div className="pt-4 border-t mt-4">
                <p className="text-sm text-muted-foreground">
                  ¿Ya verificaste tu email?{" "}
                  <Link
                    href="/login"
                    className="text-primary hover:underline font-medium"
                  >
                    Inicia sesión aquí
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
